import { useState } from "react";

const INITIAL_VALUES = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

// ── Validators ────────────────────────────────────────────────
function validate(values) {
  const errors = {};

  if (!values.name.trim()) {
    errors.name = "Full name is required.";
  } else if (values.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters.";
  }

  if (!values.email.trim()) {
    errors.email = "Email address is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (values.phone && !/^[+\d\s\-()]{7,15}$/.test(values.phone)) {
    errors.phone = "Please enter a valid phone number.";
  }

  if (!values.message.trim()) {
    errors.message = "Message cannot be empty.";
  } else if (values.message.trim().length < 10) {
    errors.message = "Message must be at least 10 characters.";
  }

  return errors;
}

// ── Simulated API call ────────────────────────────────────────
// Submit the contact form to the backend API.
async function submitContactForm(payload) {
  const API_URL = process.env.REACT_APP_API_URL || "https://makebeetrip.com";
  
  const res = await fetch(`${API_URL}/api/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to send message");
  }
  
  return res.json();
}

// ── Hook ──────────────────────────────────────────────────────
export function useContactForm() {
  const [values, setValues]   = useState(INITIAL_VALUES);
  const [errors, setErrors]   = useState({});
  const [status, setStatus]   = useState("idle"); // idle | loading | success | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field on change
    if (errors[name]) {
      setErrors((prev) => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = document.querySelector(".cf-field--error input, .cf-field--error textarea");
      if (firstErrorField) firstErrorField.focus();
      return;
    }

    setErrors({});
    setStatus("loading");

    try {
      await submitContactForm(values);
      setStatus("success");
      setValues(INITIAL_VALUES);
      // Reset back to idle after 4 seconds
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return { values, errors, status, handleChange, handleSubmit };
}

